/* eslint-disable react/require-default-props */
import React from 'react';
import { motion } from 'framer-motion';
import { makeStyles } from '@material-ui/core';
import { Body, Button } from '../UI';
import { IconClosePlain } from '../Icons';

const useStyles = makeStyles({
    container: {
        position: 'absolute',
        width: '90vw',
        left: ({ elementProperties }: any) =>
            `calc(50vw - ${elementProperties?.left}px)`,
        transform: ({ toolTipPlacement }: any) =>
            toolTipPlacement === 'top'
                ? 'translate(-50%, -100%)'
                : 'translateX(-50%)',
        zIndex: 1100,
        top: ({ elementProperties, toolTipPlacement }: any) =>
            toolTipPlacement === 'bottom'
                ? `${elementProperties?.height}px`
                : `0px`,
    },
    contentContainer: {
        background: 'rgba(0,0,0,0.9)',
        borderRadius: '20px',
        display: 'flex',
    },
    tooltipTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        rowGap: '5px',
        padding: '10px 1rem',
    },
    tooltipText: {
        whiteSpace: 'normal',
    },
    gotItButtonContent: {
        fontSize: 14,
        textDecoration: 'underline',
    },
    closeButton: {
        alignSelf: 'flex-start',
        display: 'flex',
        padding: '10px',
    },
    closeButtonContent: {
        display: 'flex',
    },
    tooltipImage: {
        alignSelf: 'flex-end',
        paddingTop: '10px',
        maxHeight: '105px',
    },
    arrowContainer: {
        display: 'flex',
        position: 'relative',
        left: ({ elementProperties, arrowPlacement }) => {
            if (elementProperties) {
                switch (arrowPlacement) {
                    case 'left':
                        return `${elementProperties.left + 10}px`;
                    case 'middle':
                        return `${
                            elementProperties.left + elementProperties.width / 2
                        }px`;
                    case 'right':
                        return `${
                            elementProperties.left + elementProperties.width
                        }px`;
                    default:
                        return 0;
                }
            }
            return 0;
        },
        transform: ({ toolTipPlacement }) =>
            toolTipPlacement === 'top'
                ? 'scale(-1, -1) translateX(100%)'
                : 'translateX(-100%)',
    },
});

interface ToolTipProps {
    arrowPlacement?: 'left' | 'middle' | 'right';
    toolTipPlacement: 'top' | 'bottom';
    tooltipText: string;
    tooltipImgSrc?: string;
    elementRef: React.RefObject<HTMLElement>;
    children: JSX.Element;
    showTooltip: boolean;
    onClose: () => void;
    containerClassName?: string;
}

function Tooltip({
    toolTipPlacement,
    arrowPlacement = 'middle',
    tooltipText,
    tooltipImgSrc,
    elementRef,
    children,
    showTooltip,
    onClose,
    containerClassName,
}: ToolTipProps) {
    const elementProperties: DOMRect | undefined =
        elementRef?.current?.getBoundingClientRect();

    // const [elementPropertiesState, setElementPropertiesState] =
    //     useState(elementProperties);

    // const [showTooltip, setShowTooltip] = useState(false);

    // useEffect(() => {
    //     setShowTooltip(false);

    //     const timeout = setTimeout(() => {
    //         setShowTooltip(true);
    //         setElementPropertiesState(elementProperties);
    //     }, 200);

    //     return () => {
    //         clearTimeout(timeout);
    //     };
    // }, [
    //     elementProperties?.left,
    //     elementProperties?.width,
    //     elementProperties?.bottom,
    // ]);

    const classes = useStyles({
        elementProperties,
        toolTipPlacement,
        arrowPlacement,
    });

    if (!elementProperties) return children || null;

    if (showTooltip) {
        return (
            <span
                style={{ position: 'relative' }}
                className={containerClassName}
            >
                {children}
                <motion.div
                    className={classes.container}
                    onViewportLeave={() => onClose()}
                >
                    {toolTipPlacement === 'bottom' && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="44"
                            height="20"
                            viewBox="0 0 44 20"
                            fill="none"
                            className={classes.arrowContainer}
                        >
                            <path
                                d="M20.5178 19.9203L43.8407 19.9203L25.4559 1.53549C23.5033 -0.417133 20.3375 -0.417133 18.3849 1.53549L9.89659e-05 19.9203L20.5178 19.9203Z"
                                fill="black"
                                fillOpacity="0.9"
                            />
                        </svg>
                    )}
                    <div className={classes.contentContainer}>
                        <div className={classes.tooltipTextContainer}>
                            <Body
                                className={classes.tooltipText}
                                color="textWhite"
                                size={16}
                            >
                                {tooltipText}
                            </Body>
                            <Button
                                contentClassName={classes.gotItButtonContent}
                                onClick={onClose}
                                variant="text"
                            >
                                Got it
                            </Button>
                        </div>
                        {tooltipImgSrc && (
                            <img
                                className={classes.tooltipImage}
                                src={tooltipImgSrc}
                                alt="tooltip"
                            />
                        )}
                        <Button
                            className={classes.closeButton}
                            contentClassName={classes.closeButtonContent}
                            variant="icon"
                            onClick={onClose}
                        >
                            <IconClosePlain fillColor="fillWhite" />
                        </Button>
                    </div>
                    {toolTipPlacement === 'top' && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="44"
                            height="20"
                            viewBox="0 0 44 20"
                            fill="none"
                            className={classes.arrowContainer}
                        >
                            <path
                                d="M20.5178 19.9203L43.8407 19.9203L25.4559 1.53549C23.5033 -0.417133 20.3375 -0.417133 18.3849 1.53549L9.89659e-05 19.9203L20.5178 19.9203Z"
                                fill="black"
                                fillOpacity="0.9"
                            />
                        </svg>
                    )}
                </motion.div>
            </span>
        );
    }

    return children || null;
}

export default Tooltip;
